/**
 * 🟥 CUADRO NEGRO 2026-02 — Detección de intención de "Revisión".
 *
 * Se invoca antes de mandar el mensaje al chat: si el mensaje del usuario
 * contiene palabras clave de revisión (es/en), ruteamos al endpoint
 * POST /api/review/chat en lugar del flujo normal POST /api/chat.
 *
 * La lista de keywords coincide con la del backend (routes/review.py) para
 * que la detección quede sincronizada de ambos lados.
 */

const REVIEW_KEYWORDS_ES = [
  'revisa', 'revisar', 'revisión', 'revision',
  'evalúa', 'evalua', 'evaluar', 'evaluación', 'evaluacion',
  'analiza', 'analizar', 'análisis', 'analisis',
  // 🟥 iter470 (2026-02-23) · 'corrige/corregir/corrección' se REMOVIERON:
  // son verbos de MODIFICACIÓN, no de revisión narrativa. Antes, un pedido
  // como "revisa los cuadros y corrijas el error" era ruteado a
  // /api/review/chat y DamyxIA respondía con 5 secciones de auditoría
  // ("## Resumen general / ## Puntos fuertes / ## Problemas detectados /
  // ## Recomendaciones / ## Conclusión") en lugar de corregir el código.
  // Marcelo reportó este fallo exacto. Ahora "corrige/corrijas" cae en el
  // flujo normal /api/chat donde el project_enforcer (iter469) lo detecta
  // como modificación y el guard de iter466 fuerza fence si falta.
  'opina', 'opinión', 'opinion',
  'diagnóstico', 'diagnostico',
  // 🟥 2026-02-05 · 'audita/auditar/auditoría' se REMOVIERON de acá:
  // ahora "audit*" gatilla la Auditoría Visual E2E (screenshots reales)
  // vía short-circuit en /api/chat. Ver routes/audit_e2e.py y
  // hooks/app/useChatSend.js — el mensaje pasa por /api/chat cuando hay
  // active_module_id y el backend responde con metadata.audit_screenshots.
  'qué te parece', 'que te parece',
  'está bien', 'esta bien',
  'está correcto', 'esta correcto',
];

const REVIEW_KEYWORDS_EN = [
  'review', 'reviewing', 'evaluate', 'evaluation', 'assess', 'assessment',
  'analyze', 'analyse', 'analysis',
  // 🟥 2026-02-05 · 'audit/auditing' se removieron acá (ver comment ES arriba).
  'check this', 'check my', 'is this correct', 'is this ok',
  'feedback on', 'your opinion', 'your thoughts',
  // 🟥 iter470 · 'fix this' se REMOVIÓ: es verbo de modificación.
  'what do you think',
];

// 🟥 iter470 (2026-02-23) · GUARD ANTI-AMBIGÜEDAD
// Si el mensaje contiene cualquier verbo FUERTE de modificación/corrección,
// NO es una review narrativa aunque también incluya "revisa" o "analiza".
// Caso del bug de Marcelo: "revisa los cuadros ... quiero que CORRIJAS ese
// error ... para poder cargar imagenes". El verbo rector real es "corrijas".
const STRONG_MODIFY_VERBS_RE = new RegExp(
  '\\b(?:' +
    // Corregir / arreglar / reparar / solucionar / resolver / debug
    'corrige|corregir|corrig[iíaá]|corr[ií]j[aá]s?|corr[ií]jalo|corr[ií]gelo|corr[ií]gel[ao]s|' +
    'arregla|arreglar|arreglal[oa]|arr[eé]glal[oa]|' +
    'repara|reparar|reparal[oa]|' +
    'soluciona|solucionar|solucional[oa]|' +
    'resuelve|resolver|resu[eé]lvel[oa]|resolvel[oa]|' +
    'depura|depurar|debuguea|debuguear|' +
    'fix|repair|solve|resolve|debug|' +
    // Verbos de cambio directo
    'cambia|cambiar|cambi[aá]l[oa]|modifica|modificar|modif[ií]cal[oa]|' +
    'actualiza|actualizar|actual[ií]zal[oa]|' +
    'agrega|agregar|agr[eé]gal[oa]|a[ñn]ade|a[ñn]adir|a[ñn][aá]del[oa]|' +
    'quita|quitar|qu[ií]tal[oa]|elimina|eliminar|elim[ií]nal[oa]|' +
    'saca|sacar|s[aá]cal[oa]|reemplaza|reemplazar|reempl[aá]zal[oa]|' +
    'mueve|mover|m[uú][eé]vel[oa]|movel[oa]|' +
    'pone|poner|p[oó]nel[oa]|' +
    // Frases
    'hac[eé]\\s+que\\s+funcione|haz\\s+que\\s+funcione|' +
    'hac[eé]\\s+que\\s+ande|haz\\s+que\\s+ande|' +
    'hacer\\s+funcionar|hacer\\s+que\\s+(?:funcione|ande)' +
  ')\\b',
  'iu',
);

export function detectReviewIntent(message) {
  if (!message || typeof message !== 'string') return false;
  // 🟥 2026-02-05 · Normalizar diacríticos: 'revisá' → 'revisa', 'opiná'
  // → 'opina'. Antes las formas de voseo con tilde no matcheaban.
  const lower = message.toLowerCase().trim();
  if (!lower) return false;
  const normalized = lower.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  // 🟥 2026-02-05 · Si el mensaje pide claramente una AUDITORÍA VISUAL
  // (screenshots/capturas/visualmente) NO lo tratamos como revisión textual
  // aunque incluya "revisá/revisa". La auditoría visual gana.
  if (/\b(screenshots?|capturas?|visualmente|de\s+forma\s+visual)\b/i.test(lower)) {
    return false;
  }
  // 🟥 iter470 (2026-02-23) · Si el mensaje contiene un verbo FUERTE de
  // modificación, el pedido es MODIFICAR, no revisar. Aunque también haya
  // "revisa", el verbo rector gana. Esto resuelve el bug reportado por
  // Marcelo: "revisa los cuadros … quiero que CORRIJAS ese error".
  if (STRONG_MODIFY_VERBS_RE.test(lower) || STRONG_MODIFY_VERBS_RE.test(normalized)) {
    return false;
  }
  for (const kw of REVIEW_KEYWORDS_ES) {
    // Comparar contra ambos: la forma con tilde (lower) y sin tilde (normalized)
    const kwNorm = kw.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    if (lower.includes(kw) || normalized.includes(kwNorm)) return true;
  }
  for (const kw of REVIEW_KEYWORDS_EN) {
    if (lower.includes(kw)) return true;
  }
  return false;
}

export default detectReviewIntent;
